import React, { useEffect } from "react";
import { Button, Form, FormProps, Input, message, Select } from "antd";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks.ts";
import { useNavigate } from "react-router-dom";
import { onRegister } from "../../../redux/actions/authActions.tsx";
import { validatePassword } from "../../../utils/auth/commonValidators.ts";
import { useIsMobile } from "../../../hooks/useIsMobile.ts";
import { extractErrorMessage } from "../../../utils/extractErrorMessage.ts";

type FormValues = {
  name: string;
  email: string;
  password: string;
  group: string;
};

const RegisterImage: React.FC = () => (
  <img
    src="/register-image.png"
    className="max-md:w-4/5 mx-auto block object-cover"
    alt="Login image"
    width={0}
    height={0}
    style={{ width: "100%" }}
  />
);

export const RegisterForm: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.auth);

  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    form.setFieldValue("group", "admin");
  }, []);

  const onRegisterForm: FormProps<FormValues>["onFinish"] = async (
    registerData,
  ) => {
    try {
      await dispatch(onRegister({ ...registerData })).unwrap();

      messageApi.success("Usuario registrado correctamente.");
      form.resetFields();

      setTimeout(() => navigate("/users"), 1500);
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo registrar el usuario."),
      );
    }
  };

  return (
    <div className="font-[sans-serif] bg-white">
      {contextHolder}
      <div className="min-h-screen flex flex-col items-center justify-center py-6 px-4">
        <div className="grid md:grid-cols-2 items-center gap-4 max-w-6xl w-full">
          <div className="border border-gray-300 rounded-lg p-6 max-w-md shadow-[0_2px_22px_-4px_rgba(93,96,127,0.2)] max-md:mx-auto">
            <Form form={form} className="space-y-4" onFinish={onRegisterForm}>
              <div className="mb-8 mx-auto text-center">
                <h6 className="text-gray-800 text-2xl font-extrabold text-center">
                  Registro de usuarios
                </h6>
              </div>

              {isMobile && <RegisterImage />}

              <div className="flex flex-col">
                <Form.Item<FormValues>
                  label="Nombre"
                  labelCol={{ span: 24 }}
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: "Campo requerido.",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>

                <Form.Item<FormValues>
                  label="Email"
                  labelCol={{ span: 24 }}
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: "Campo requerido.",
                    },
                    {
                      type: "email",
                      message: "Ingresa un email válido.",
                    },
                  ]}
                >
                  <Input type="email" className="lowercase" />
                </Form.Item>

                <Form.Item<FormValues>
                  label="Contraseña"
                  labelCol={{ span: 24 }}
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: "Campo requerido.",
                    },
                    { validator: validatePassword },
                  ]}
                >
                  <Input.Password />
                </Form.Item>

                <Form.Item<FormValues>
                  label="Grupo"
                  labelCol={{ span: 24 }}
                  name="group"
                  rules={[
                    {
                      required: true,
                      message: "Campo requerido.",
                    },
                  ]}
                >
                  <Select placeholder="Selecciona un grupo">
                    <Select.Option value="admin">Administrador</Select.Option>
                    <Select.Option value="operations">
                      Operaciones
                    </Select.Option>
                    <Select.Option value="cja">CJA</Select.Option>
                    <Select.Option value="observer">Observador</Select.Option>
                    <Select.Option value="assistant">Asistente</Select.Option>
                  </Select>
                </Form.Item>
              </div>

              <div className="!mt-8">
                <Button
                  type="primary"
                  loading={loading}
                  htmlType="submit"
                  className="w-full shadow-xl py-6 px-7 text-sm tracking-wide rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                >
                  Registrar usuario
                </Button>
              </div>
            </Form>
          </div>
          <div className="lg:h-[400px] md:h-[300px] max-md:mt-8 flex justify-center items-center">
            {!isMobile && <RegisterImage />}
          </div>
        </div>
      </div>
    </div>
  );
};
